import { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@scio3/ui';
import { Trash2, Check } from 'lucide-react';
import {
  GET_STRIPE_CUSTOMER_TAXABLE,
  UPDATE_STRIPE_TAX,
  DELETE_STRIPE_TAX,
} from '@scio3/graphql-client';
import { toast } from 'sonner';
import { useConfirm } from '@/components/shared';
import { TAX_ID_TYPES, TAX_ID_SAMPLES } from '../utils/billing-constants';
import { COUNTRIES } from '@/features/settings/utils/profile-constants';

/** Convert tax code to display name matching scio1's getCountryName().
 *  e.g. 'ae_trn' → 'AE TRN - United Arab Emirates' */
function getTaxDisplayName(code: string): string {
  const parts = code.toUpperCase().split('_');
  const prefix = parts.join(' ');
  // Special cases
  if (code === 'eu_oss_vat') return `${prefix} - European Union`;
  if (code === 'eu_vat') return `${prefix} - European`;
  // Look up country by first part
  const countryCode = parts[0];
  const country = COUNTRIES.find((c) => c.code === countryCode);
  return country ? `${prefix} - ${country.name}` : prefix;
}

type TaxId = {
  id: string;
  code: string;
  value: string;
  type: string; // 'edit' for existing, 'new' for new
};

type TaxIdSectionProps = {
  /** Initial tax IDs from parent query */
  taxIds?: TaxId[];
  /** Sub-account ID */
  subAccountId?: string;
  /** Team ID */
  teamId?: string;
  /** Called after any change to refresh data */
  onChanged?: () => void;
};

type TaxRow = {
  code: string;
  value: string;
  id?: string;
  type: 'NEW' | 'edit';
  /** Track original values to detect changes */
  originalCode?: string;
  originalValue?: string;
};

/**
 * Tax ID management section matching scio1 layout.
 * Uses Select dropdown for tax type (Stripe-supported types).
 * Inline save/delete per row + "Add another ID" button.
 */
export function TaxIdSection({
  taxIds: initialTaxIds,
  subAccountId,
  teamId,
  onChanged,
}: TaxIdSectionProps) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [isEditing, setIsEditing] = useState(false);
  const [taxIds, setTaxIds] = useState<TaxId[]>(initialTaxIds ?? []);
  const [editRows, setEditRows] = useState<TaxRow[]>([]);

  const [fetchTaxIds] = useLazyQuery(GET_STRIPE_CUSTOMER_TAXABLE, {
    fetchPolicy: 'network-only',
  });

  const [updateTax] = useMutation(UPDATE_STRIPE_TAX);
  const [deleteTax] = useMutation(DELETE_STRIPE_TAX);

  const refreshTaxIds = async () => {
    const result = await fetchTaxIds({ variables: { subAccountId, teamId } });
    const fresh = (result.data as Record<string, unknown> | undefined)?.getStripeCustomerTaxable;
    if (fresh) setTaxIds(fresh as TaxId[]);
    onChanged?.();
  };

  const handleStartEdit = () => {
    // Convert existing tax IDs to edit rows with original values for dirty tracking
    const rows: TaxRow[] = taxIds.map((tax) => ({
      code: tax.code,
      value: tax.value,
      id: tax.id,
      type: 'edit' as const,
      originalCode: tax.code,
      originalValue: tax.value,
    }));
    setEditRows(rows);
    setIsEditing(true);
  };

  const handleAddRow = () => {
    if (editRows.length >= 5) {
      toast.error(t('Maximum 5 tax IDs allowed'));
      return;
    }
    setEditRows((prev) => [...prev, { code: '', value: '', type: 'NEW' }]);
  };

  const handleSaveRow = async (index: number) => {
    const row = editRows[index];
    if (!row.code) {
      toast.error(t('Please select a tax ID type.'));
      return;
    }
    if (!row.value.trim()) {
      toast.error(t('Tax is required.'));
      return;
    }

    try {
      const saveResult = await updateTax({
        variables: {
          tax: { code: row.code, value: row.value, type: row.type.toUpperCase(), id: row.id },
          subAccountId,
          teamId,
        },
      });
      const response = saveResult.data as Record<string, unknown> | undefined;
      const taxResult = response?.updateStripeTax as
        | { status?: boolean; message?: string; tax?: TaxId }
        | undefined;
      if (taxResult && !taxResult.status) {
        // Format error: replace raw tax code with display name (matching scio1 line 241)
        let message = taxResult.message ?? t('Failed to save tax ID');
        const codeMatch = message.match(/(\w+_\w+)\.?$/);
        if (codeMatch?.[1]) {
          const displayName = getTaxDisplayName(codeMatch[1]);
          message = t('Invalid value for {{name}}.', { name: displayName });
        }
        toast.error(message);
        return;
      }
      toast(t('Your tax has been saved!'));
      // Refresh edit rows from updated taxIds — set originals so dirty tracking resets
      const result = await fetchTaxIds({ variables: { subAccountId, teamId } });
      const fresh = (result.data as Record<string, unknown> | undefined)
        ?.getStripeCustomerTaxable as TaxId[] | undefined;
      if (fresh) {
        setTaxIds(fresh);
        setEditRows(
          fresh.map((tax) => ({
            code: tax.code,
            value: tax.value,
            id: tax.id,
            type: 'edit' as const,
            originalCode: tax.code,
            originalValue: tax.value,
          })),
        );
      }
    } catch {
      toast.error(t('Failed to save tax ID'));
    }
  };

  const handleDeleteRow = async (index: number) => {
    const row = editRows[index];
    if (row.id) {
      // Existing tax — confirm delete
      const confirmed = await confirm({
        title: t('Delete Tax ID'),
        description: t('Are you sure you want to remove this tax ID?'),
      });
      if (!confirmed) return;

      try {
        await deleteTax({ variables: { taxId: row.id, subAccountId, teamId } });
        toast(t('Tax ID removed'));
        await refreshTaxIds();
      } catch {
        toast.error(t('Failed to remove tax ID'));
      }
    }
    // Remove from edit rows
    setEditRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCancel = () => {
    setEditRows([]);
    setIsEditing(false);
  };

  // View mode
  if (!isEditing) {
    return (
      <div className="space-y-4" data-testid="payment-tax-view">
        {taxIds.length > 0 ? (
          <div className="space-y-2" data-testid="payment-tax-list">
            {taxIds.map((tax) => (
              <p key={tax.id} className="text-sm" data-testid={`payment-tax-row-display-${tax.id}`}>
                {getTaxDisplayName(tax.code)} {tax.value}
              </p>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground italic">{t('No tax IDs configured')}</p>
        )}
        <div className="flex justify-center">
          <Button
            onClick={handleStartEdit}
            className="cursor-pointer"
            data-testid="payment-tax-update-btn"
          >
            {t('Update Tax')}
          </Button>
        </div>
      </div>
    );
  }

  // Edit mode — matching scio1: select + input + save/delete per row
  return (
    <div className="space-y-4" data-testid="payment-tax-edit">
      {editRows.map((row, index) => {
        const isDirty =
          row.type === 'NEW' || row.code !== row.originalCode || row.value !== row.originalValue;

        return (
          <div
            key={index}
            className="flex items-end gap-3"
            data-testid={`payment-tax-row-${index}`}
          >
            {/* Tax type dropdown */}
            <div className="flex-1">
              <Select
                value={row.code || '__placeholder__'}
                onValueChange={(v) => {
                  if (v === '__placeholder__') return;
                  setEditRows((prev) => prev.map((r, i) => (i === index ? { ...r, code: v } : r)));
                }}
              >
                <SelectTrigger
                  className="cursor-pointer"
                  data-testid={`payment-tax-type-trigger-${index}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__placeholder__" disabled className="text-muted-foreground">
                    {t('ID type')}
                  </SelectItem>
                  {TAX_ID_TYPES.map((taxType) => (
                    <SelectItem
                      key={taxType.code}
                      value={taxType.code}
                      data-testid={`payment-tax-type-option-${taxType.code}`}
                    >
                      {taxType.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tax value input with sample placeholder */}
            <div className="flex-1">
              <Input
                data-testid={`payment-tax-value-input-${index}`}
                value={row.value}
                onChange={(e) =>
                  setEditRows((prev) =>
                    prev.map((r, i) => (i === index ? { ...r, value: e.target.value } : r)),
                  )
                }
                placeholder={row.code ? TAX_ID_SAMPLES[row.code] ?? '' : ''}
              />
            </div>

            {/* Save button — disabled when no changes */}
            <Button
              size="icon-md"
              onClick={() => handleSaveRow(index)}
              disabled={!isDirty || !row.code || !row.value.trim()}
              aria-label={t('Save tax ID')}
              data-testid={`payment-tax-save-btn-${index}`}
              className={
                isDirty && row.code && row.value.trim()
                  ? 'cursor-pointer shrink-0'
                  : 'shrink-0 opacity-50 cursor-not-allowed'
              }
            >
              <Check className="h-4 w-4" />
            </Button>

            {/* Delete button */}
            <Button
              variant="outline"
              size="icon-md"
              onClick={() => handleDeleteRow(index)}
              aria-label={t('Delete tax ID')}
              data-testid={`payment-tax-delete-btn-${index}`}
              className="cursor-pointer shrink-0 text-destructive border-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      })}

      {/* Add another ID */}
      {editRows.length < 5 && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleAddRow}
          className="cursor-pointer"
          data-testid="payment-tax-add-row-btn"
        >
          + {t('Add another ID')}
        </Button>
      )}

      {/* Cancel button centered */}
      <div className="flex justify-center">
        <Button
          variant="outline"
          onClick={handleCancel}
          className="cursor-pointer"
          data-testid="payment-tax-cancel-btn"
        >
          {t('Cancel')}
        </Button>
      </div>
    </div>
  );
}
